import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, useParams } from 'react-router';
import { Spinner } from '@vwt/ui';
import { AppShell } from '../components/shell.js';
import { useSession } from '../lib/session.js';
import { AuthErrorPage, KeyRedirect, NotFoundPage, RouteErrorPage, ServiceErrorPage, SignedOutPage, SignInRedirect } from '../features/auth/auth-pages.js';
import { MyWorkPage } from '../features/my-work/my-work-page.js';
import { WorkListPage } from '../features/work/work-list-page.js';
import { WorkDetail } from '../features/work/work-detail.js';
import { RequestsPage } from '../features/requests/requests-page.js';
import { RequestDetail } from '../features/requests/request-detail.js';
import { IntakePage } from '../features/intake/intake-page.js';
import { ProjectPage, ProjectsPage } from '../features/projects/projects-pages.js';
import { TeamPage, TeamsPage } from '../features/teams/teams-pages.js';
import { SettingsPage } from '../features/settings/settings-page.js';
import { SlackLinkPage } from '../features/settings/slack-link-page.js';
import { InboxPage } from '../features/inbox/inbox-page.js';
import { TutorialPage } from '../features/tutorial/tutorial-page.js';

// Heavier, less frequent screens load on demand.
const AdminPage = lazy(() => import('../features/admin/admin-page.js').then((m) => ({ default: m.AdminPage })));
const ReportsPage = lazy(() => import('../features/reports/reports-page.js').then((m) => ({ default: m.ReportsPage })));

function Loading() {
  return (
    <div className="flex h-full items-center justify-center">
      <Spinner />
    </div>
  );
}

/** Requires an authenticated session; anonymous visitors go through corporate sign-in. */
function Protected() {
  const { status } = useSession();
  if (status === 'loading') return <Loading />;
  if (status === 'anonymous') return <SignInRedirect />;
  if (status === 'error') return <ServiceErrorPage />;
  return <AppShell />;
}

function WorkPage() {
  const { id } = useParams<{ id: string }>();
  return (
    <div className="mx-auto h-full max-w-6xl bg-surface md:border-x md:border-border">
      <WorkDetail id={id!} />
    </div>
  );
}

function RequestPage() {
  const { id } = useParams<{ id: string }>();
  return (
    <div className="mx-auto h-full max-w-6xl bg-surface md:border-x md:border-border">
      <RequestDetail id={id!} />
    </div>
  );
}

export const router = createBrowserRouter([
  { path: '/auth-error', element: <AuthErrorPage />, errorElement: <RouteErrorPage /> },
  { path: '/signed-out', element: <SignedOutPage /> },
  {
    element: <Protected />,
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <Navigate to="/my-work" replace /> },
      { path: '/my-work', element: <MyWorkPage /> },
      { path: '/requests', element: <RequestsPage /> },
      { path: '/requests/new', element: <IntakePage /> },
      { path: '/requests/:id', element: <RequestPage /> },
      { path: '/work', element: <WorkListPage /> },
      { path: '/work/:id', element: <WorkPage /> },
      { path: '/projects', element: <ProjectsPage /> },
      { path: '/projects/:id', element: <ProjectPage /> },
      { path: '/teams', element: <TeamsPage /> },
      { path: '/teams/:id', element: <TeamPage /> },
      { path: '/settings', element: <SettingsPage /> },
      { path: '/inbox', element: <InboxPage /> },
      { path: '/slack/link', element: <SlackLinkPage /> },
      { path: '/tutorial', element: <TutorialPage /> },
      {
        element: (
          <Suspense fallback={<Loading />}>
            <Outlet />
          </Suspense>
        ),
        children: [
          { path: '/reports', element: <ReportsPage /> },
          { path: '/admin', element: <AdminPage /> },
        ],
      },
      { path: '/go/:key', element: <KeyRedirect /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
